import { type ReactNode, createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { ApiError, api, setAccessToken, setRefreshHook } from "./api-client";
import { AUTH_ENDPOINTS, LOGIN_URL, type TokenResponse, rememberReturnTo } from "./auth-config";
import type { UserProfile } from "./types";

/**
 * Auth context.
 *
 * The tools site does not own authentication. The flow, as the auth worker at
 * auth.railassurance.co.uk actually implements it:
 *
 *   1. login() sends the browser to the worker's /login page. That page takes
 *      no redirect parameter — it routes by org membership, server-side.
 *   2. On success it redirects to /auth/callback?token=… where `token` is a
 *      single-use, 60-second *exchange* token, NOT the access token.
 *   3. We POST that to /api/auth/exchange and get back a 15-minute Ed25519
 *      access JWT (held in memory only) plus the user profile. The worker also
 *      sets an HttpOnly refresh cookie on the auth domain.
 *   4. /api/auth/refresh swaps that cookie for a fresh access token. It is
 *      cross-origin but same-site, so the SameSite=Lax cookie rides along with
 *      credentials: "include", and the worker's CORS allows *.railassurance.co.uk.
 */
interface AuthState {
  user: UserProfile | null;
  isAdmin: boolean;
  loading: boolean;
  error: string | null;
  authenticated: boolean;
  login: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

/** Refresh this many seconds before the token actually expires. */
const REFRESH_SKEW_SECONDS = 60;

/**
 * Exchange tokens are single-use. React StrictMode double-invokes effects in
 * development, so dedupe by token value at module scope — a component-level
 * guard is reset by the remount and would burn the token on the second pass.
 */
const exchangesInFlight = new Map<string, Promise<TokenResponse | null>>();

async function exchangeToken(token: string): Promise<TokenResponse | null> {
  const existing = exchangesInFlight.get(token);
  if (existing) return existing;

  const run = (async () => {
    try {
      const res = await fetch(AUTH_ENDPOINTS.exchange, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ token }),
      });
      if (!res.ok) return null;
      return (await res.json()) as TokenResponse;
    } catch {
      return null;
    }
  })();

  exchangesInFlight.set(token, run);
  return run;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [authenticated, setAuthenticated] = useState(false);

  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshInFlight = useRef<Promise<string | null> | null>(null);
  // Indirection so the scheduled timer can call the latest refresh without
  // adoptToken and doRefresh depending on each other directly.
  const doRefreshRef = useRef<() => Promise<string | null>>(async () => null);

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimer.current !== null) {
      clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    }
  }, []);

  /** Adopt a token response: hold the token and queue the next silent refresh. */
  const adoptToken = useCallback(
    (payload: TokenResponse) => {
      setAccessToken(payload.access_token);
      clearRefreshTimer();
      const delay = Math.max(payload.expires_in - REFRESH_SKEW_SECONDS, 30) * 1000;
      refreshTimer.current = setTimeout(() => {
        void doRefreshRef.current();
      }, delay);
    },
    [clearRefreshTimer],
  );

  /**
   * Swap the HttpOnly refresh cookie for a new access token. Deduped, because
   * several in-flight API calls can hit 401 at once and all ask to refresh.
   */
  const doRefresh = useCallback((): Promise<string | null> => {
    if (refreshInFlight.current) return refreshInFlight.current;

    const run = (async () => {
      try {
        const res = await fetch(AUTH_ENDPOINTS.refresh, {
          method: "POST",
          credentials: "include",
        });
        if (!res.ok) {
          setAccessToken(null);
          clearRefreshTimer();
          return null;
        }
        const payload = (await res.json()) as TokenResponse;
        adoptToken(payload);
        return payload.access_token;
      } catch {
        return null;
      } finally {
        refreshInFlight.current = null;
      }
    })();

    refreshInFlight.current = run;
    return run;
  }, [adoptToken, clearRefreshTimer]);

  useEffect(() => {
    doRefreshRef.current = doRefresh;
  }, [doRefresh]);

  // Let the API client trigger a silent refresh + replay when it hits a 401.
  useEffect(() => {
    setRefreshHook(doRefresh);
    return () => setRefreshHook(null);
  }, [doRefresh]);

  useEffect(() => {
    let active = true;

    (async () => {
      try {
        // ── 1. Returning from the auth worker? Spend the exchange token. ──────
        const url = new URL(window.location.href);
        const exchange = url.pathname === "/auth/callback" ? url.searchParams.get("token") : null;

        if (exchange) {
          const payload = await exchangeToken(exchange);
          if (!active) return;
          if (payload?.access_token) adoptToken(payload);
          // Strip the token from the URL either way, before it can leak through
          // history, a Referer header or a bookmark. The path stays put so the
          // router still matches /auth/callback and can forward the user on.
          // A failed exchange (past its 60s, or already spent) falls through to
          // the /me call below, 401s, and lands them on the sign-in screen.
          window.history.replaceState({}, "", "/auth/callback");
        } else {
          // ── 2. Otherwise try to resume an existing session silently. ────────
          await doRefresh();
          if (!active) return;
        }

        // ── 3. Load the profile. In dev the API provisions a stand-in user. ───
        const res = await api.me();
        if (!active) return;
        setUser(res.user);
        setIsAdmin(Boolean(res.isAdmin));
        setAuthenticated(true);
      } catch (e: unknown) {
        if (!active) return;
        // A 401 here means "not signed in" — a normal state, not an error to
        // shout about. The gate renders the sign-in screen instead.
        if (e instanceof ApiError && e.status === 401) {
          setAuthenticated(false);
        } else {
          setError(e instanceof Error ? e.message : "Failed to load session");
        }
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
    // Bootstrap runs once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => clearRefreshTimer, [clearRefreshTimer]);

  const login = useCallback(() => {
    rememberReturnTo(window.location.pathname + window.location.search);
    window.location.href = LOGIN_URL;
  }, []);

  const logout = useCallback(() => {
    clearRefreshTimer();
    // Revokes the refresh token and bumps token_version, which invalidates any
    // outstanding access tokens immediately.
    void fetch(AUTH_ENDPOINTS.logout, { method: "POST", credentials: "include" })
      .catch(() => undefined)
      .finally(() => {
        setAccessToken(null);
        setUser(null);
        setIsAdmin(false);
        setAuthenticated(false);
      });
  }, [clearRefreshTimer]);

  return (
    <AuthContext.Provider value={{ user, isAdmin, loading, error, authenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
